import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { getConfig } from 'config/config-helper';
import { RootState } from 'app/store';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface IGroupMapping {
  gitlab_group_id: number;
  gitlab_group_path: string | null;
  access_level: string;
  enabled: boolean;
}

export interface IGitlabGroup {
  id: number;
  name: string;
  full_path: string;
}

export interface IScimGroupMember {
  id: number;
  email: string;
  name: string | null;
}

export interface IScimGroupList {
  id: number;
  display_name: string;
  scim_external_id: string;
  member_count: number;
  mapping_count: number;
}

export interface IScimGroupDetail {
  id: number;
  display_name: string;
  scim_external_id: string;
  group_mappings: IGroupMapping[];
  members: IScimGroupMember[];
}

export interface IScimManager {
  member_id: number;
  email: string;
  name: string | null;
}

export interface IOrgMember {
  id: number;
  email: string;
  name: string | null;
  is_scim_manager: boolean;
}

export interface IPermissions {
  can_manage_scim_managers: boolean;
  can_manage_scim_groups: boolean;
  is_superuser: boolean;
}

export interface ScimManagementState {
  groups: IScimGroupList[];
  currentGroup: IScimGroupDetail | null;
  managers: IScimManager[];
  orgMembers: IOrgMember[];
  permissions: IPermissions | null;
  gitlabGroups: IGitlabGroup[];
  gitlabGroupsLoading: boolean;
  loading: boolean;
  groupDetailLoading: boolean;
  error: string | null;
  deleteConfirmGroupId: number | null;
  syncingGroupIds: number[];
}

const initialState: ScimManagementState = {
  groups: [],
  currentGroup: null,
  managers: [],
  orgMembers: [],
  permissions: null,
  gitlabGroups: [],
  gitlabGroupsLoading: false,
  loading: false,
  groupDetailLoading: false,
  error: null,
  deleteConfirmGroupId: null,
  syncingGroupIds: [],
};

// ---------------------------------------------------------------------------
// Async thunks
// ---------------------------------------------------------------------------

const BASE = '/api/v1/scim/admin';

export const fetchGitlabGroups = createAsyncThunk(
  'scimManagement/fetchGitlabGroups',
  async ({ platformToken, search }: { platformToken: string; search?: string }) => {
    const { dataopsliveBaseUrl } = getConfig();
    const params: Record<string, unknown> = { top_level_only: false };
    if (search !== undefined && search.length > 0) {
      params.search = search;
    }
    const response = await axios.get<IGitlabGroup[]>(`${dataopsliveBaseUrl}/api/v4/groups`, {
      headers: { Authorization: `Bearer ${platformToken}` },
      params,
      withCredentials: false,
    });
    return response.data;
  },
);

export const fetchGroups = createAsyncThunk('scimManagement/fetchGroups', async () => {
  const response = await axios.get<IScimGroupList[]>(`${BASE}/groups`);
  return response.data;
});

export const createGroup = createAsyncThunk('scimManagement/createGroup', async (displayName: string) => {
  const response = await axios.post<IScimGroupDetail>(`${BASE}/groups`, { display_name: displayName });
  return response.data;
});

export const fetchGroup = createAsyncThunk('scimManagement/fetchGroup', async (groupId: number) => {
  const response = await axios.get<IScimGroupDetail>(`${BASE}/groups/${groupId}`);
  return response.data;
});

export const updateGroup = createAsyncThunk(
  'scimManagement/updateGroup',
  async (payload: { groupId: number; display_name?: string; group_mappings?: IGroupMapping[] }) => {
    const { groupId, ...data } = payload;
    const response = await axios.put<IScimGroupDetail>(`${BASE}/groups/${groupId}`, data);
    return response.data;
  },
);

export const deleteGroup = createAsyncThunk('scimManagement/deleteGroup', async (groupId: number) => {
  await axios.delete(`${BASE}/groups/${groupId}`);
  return groupId;
});

export const fetchPermissions = createAsyncThunk('scimManagement/fetchPermissions', async () => {
  const response = await axios.get<IPermissions>(`${BASE}/permissions`);
  return response.data;
});

export const fetchOrgMembers = createAsyncThunk('scimManagement/fetchOrgMembers', async (search?: string) => {
  const params = search !== undefined && search.length > 0 ? { search } : {};
  const response = await axios.get<IOrgMember[]>(`${BASE}/members`, { params });
  return response.data;
});

export const fetchManagers = createAsyncThunk('scimManagement/fetchManagers', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.get<IScimManager[]>(`${BASE}/managers`);
    return response.data;
  } catch (err: unknown) {
    if (axios.isAxiosError(err) && err.response?.status === 403) {
      return rejectWithValue(403);
    }
    return rejectWithValue(0);
  }
});

export const assignManager = createAsyncThunk(
  'scimManagement/assignManager',
  async (
    { memberId, email, organisationId }: { memberId?: number; email?: string; organisationId?: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post<IScimManager>(`${BASE}/managers`, {
        member_id: memberId,
        email,
        organisation_id: organisationId,
      });
      return response.data;
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err.response?.data ?? 'Failed to assign manager');
      }
      return rejectWithValue('Failed to assign manager');
    }
  },
);

export const revokeManager = createAsyncThunk(
  'scimManagement/revokeManager',
  async (memberId: number, { rejectWithValue }) => {
    try {
      await axios.delete(`${BASE}/managers/${memberId}`);
      return memberId;
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err.response?.data ?? 'Failed to revoke manager');
      }
      return rejectWithValue('Failed to revoke manager');
    }
  },
);

export const syncGroup = createAsyncThunk('scimManagement/syncGroup', async (groupId: number) => {
  await axios.post(`${BASE}/groups/${groupId}/sync`);
  return groupId;
});

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

export const scimManagementSlice = createSlice({
  name: 'scimManagement',
  initialState,
  reducers: {
    setDeleteConfirmGroupId: (state, action: PayloadAction<number | null>) => {
      state.deleteConfirmGroupId = action.payload;
    },
    clearCurrentGroup: (state) => {
      state.currentGroup = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchGroups
    builder.addCase(fetchGroups.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchGroups.fulfilled, (state, action) => {
      state.loading = false;
      state.groups = action.payload;
    });
    builder.addCase(fetchGroups.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message ?? 'Failed to fetch groups';
    });

    // fetchGroup
    builder.addCase(fetchGroup.pending, (state) => {
      state.groupDetailLoading = true;
    });
    builder.addCase(fetchGroup.fulfilled, (state, action) => {
      state.groupDetailLoading = false;
      state.currentGroup = action.payload;
    });
    builder.addCase(fetchGroup.rejected, (state, action) => {
      state.groupDetailLoading = false;
      state.error = action.error.message ?? 'Failed to fetch group';
    });

    // createGroup
    builder.addCase(createGroup.fulfilled, (state, action) => {
      const group = action.payload;
      state.groups.push({
        id: group.id,
        display_name: group.display_name,
        scim_external_id: group.scim_external_id,
        member_count: group.members.length,
        mapping_count: group.group_mappings.length,
      });
    });

    // updateGroup
    builder.addCase(updateGroup.pending, (state) => {
      state.groupDetailLoading = true;
    });
    builder.addCase(updateGroup.fulfilled, (state, action) => {
      state.groupDetailLoading = false;
      const updated = action.payload;
      const idx = state.groups.findIndex((g) => g.id === updated.id);
      if (idx !== -1) {
        state.groups[idx] = {
          id: updated.id,
          display_name: updated.display_name,
          scim_external_id: updated.scim_external_id,
          member_count: updated.members.length,
          mapping_count: updated.group_mappings.length,
        };
      }
      if (state.currentGroup?.id === updated.id) {
        state.currentGroup = updated;
      }
    });
    builder.addCase(updateGroup.rejected, (state) => {
      state.groupDetailLoading = false;
      state.error = 'Failed to save group. Please try again.';
    });

    // deleteGroup
    builder.addCase(deleteGroup.fulfilled, (state, action) => {
      state.groups = state.groups.filter((g) => g.id !== action.payload);
      if (state.currentGroup?.id === action.payload) {
        state.currentGroup = null;
      }
      state.deleteConfirmGroupId = null;
    });

    // fetchGitlabGroups
    builder.addCase(fetchGitlabGroups.pending, (state) => {
      state.gitlabGroupsLoading = true;
    });
    builder.addCase(fetchGitlabGroups.fulfilled, (state, action) => {
      state.gitlabGroupsLoading = false;
      state.gitlabGroups = action.payload;
    });
    builder.addCase(fetchGitlabGroups.rejected, (state) => {
      state.gitlabGroupsLoading = false;
    });

    // fetchPermissions
    builder.addCase(fetchPermissions.fulfilled, (state, action) => {
      state.permissions = action.payload;
    });
    builder.addCase(fetchPermissions.rejected, (state) => {
      state.permissions = null;
    });

    // fetchOrgMembers
    builder.addCase(fetchOrgMembers.fulfilled, (state, action) => {
      state.orgMembers = action.payload;
    });

    // fetchManagers
    builder.addCase(fetchManagers.fulfilled, (state, action) => {
      state.managers = action.payload;
    });

    // assignManager
    builder.addCase(assignManager.fulfilled, (state, action) => {
      const exists = state.managers.some((m) => m.member_id === action.payload.member_id);
      if (!exists) {
        state.managers.push(action.payload);
      }
    });

    // revokeManager
    builder.addCase(revokeManager.fulfilled, (state, action) => {
      state.managers = state.managers.filter((m) => m.member_id !== action.payload);
    });

    // syncGroup
    builder.addCase(syncGroup.pending, (state, action) => {
      state.syncingGroupIds = [...state.syncingGroupIds, action.meta.arg];
    });
    builder.addCase(syncGroup.fulfilled, (state, action) => {
      state.syncingGroupIds = state.syncingGroupIds.filter((id) => id !== action.payload);
    });
    builder.addCase(syncGroup.rejected, (state, action) => {
      state.syncingGroupIds = state.syncingGroupIds.filter((id) => id !== action.meta.arg);
    });
  },
});

export const { setDeleteConfirmGroupId, clearCurrentGroup, clearError } = scimManagementSlice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectGroups = (state: RootState): IScimGroupList[] => state.scimManagement.groups;
export const selectCurrentGroup = (state: RootState): IScimGroupDetail | null => state.scimManagement.currentGroup;
export const selectManagers = (state: RootState): IScimManager[] => state.scimManagement.managers;
export const selectOrgMembers = (state: RootState): IOrgMember[] => state.scimManagement.orgMembers;
export const selectPermissions = (state: RootState): IPermissions | null => state.scimManagement.permissions;
export const selectCanManageScimManagers = (state: RootState): boolean =>
  state.scimManagement.permissions?.can_manage_scim_managers ?? false;
export const selectCanManageScimGroups = (state: RootState): boolean =>
  state.scimManagement.permissions?.can_manage_scim_groups ?? false;
export const selectGitlabGroups = (state: RootState): IGitlabGroup[] => state.scimManagement.gitlabGroups;
export const selectGitlabGroupsLoading = (state: RootState): boolean => state.scimManagement.gitlabGroupsLoading;
export const selectScimLoading = (state: RootState): boolean => state.scimManagement.loading;
export const selectGroupDetailLoading = (state: RootState): boolean => state.scimManagement.groupDetailLoading;
export const selectScimError = (state: RootState): string | null => state.scimManagement.error;
export const selectDeleteConfirmGroupId = (state: RootState): number | null =>
  state.scimManagement.deleteConfirmGroupId;
export const selectSyncingGroupIds = (state: RootState): number[] => state.scimManagement.syncingGroupIds;

export default scimManagementSlice.reducer;
